<template>
  <div v-if="sessionLoading" class="spinner-container">
    <q-spinner-dots size="40px" color="secondary" />
  </div>
  <router-view v-else />
</template>

<script>
import { onMounted, onUnmounted, defineComponent } from "vue";
import { useRouter } from "vue-router";
import { useQuasar } from "quasar";
import { logger } from "./logger";
import { APP_VERSION } from "./version";
import { useExchangeStore } from "stores/exchange-store.js";
import { isExchangeHours } from "src/utils";
import { storeToRefs } from "pinia";
import { referralInit } from "./rum";
import { useUserStore } from "stores/user-store";

export default defineComponent({
  name: "App",
  setup() {
    const $q = useQuasar();
    const router = useRouter();
    const exchangeStore = useExchangeStore();
    const userStore = useUserStore();

    const { realtimeEnabled } = storeToRefs(exchangeStore);

    const { sessionLoading } = storeToRefs(userStore);

    exchangeStore.$subscribe((mutation, state) => {
      logger.debug("State change:", mutation.type, mutation);
      if (mutation.events?.key) {
        logger.debug(
          `State change ${mutation.events.key} ${JSON.stringify(
            mutation.events.oldValue
          )} -> ${JSON.stringify(mutation.events.newValue)})`
        );
      }
    });

    router.beforeEach((to, from, next) => {
      if (to.meta?.loading) {
        $q.loading.show();
      }
      next();
    });

    router.beforeResolve((to, from, next) => {
      $q.loading.hide();
      next();
    });

    let marketWatchInterval;
    onMounted(() => {
      logger.debug("Loaded app with version", APP_VERSION);
      referralInit();

      // Note: Auto-pause on tab visibility change removed per user feedback (FB-6)
      // Users prefer continuous streaming even when switching tabs

      if (import.meta.env.PROD) {
        setInterval(async () => {
          try {
            const response = await fetch("/version.json", {
              cache: "no-store",
            });
            const data = await response.json();
            logger.info("Checking versions", data.version, APP_VERSION);
            if (response.ok && data.version !== APP_VERSION) {
              // New version available
              window.location.reload(); // Force reload
            } else {
              logger.debug("No new version available");
            }
          } catch (error) {
            logger.error("Error fetching version:", error);
          }
        }, 60000); // Check every 60 seconds, adjust as needed
      }

      marketWatchInterval = setInterval(() => {
        if (realtimeEnabled.value) {
          if (!isExchangeHours()) {
            logger.debug("Outside exchange hours");
            exchangeStore.$patch({ realtimeEnabled: false });
          }
        }
      }, 60000);
    });

    onUnmounted(() => {
      if (marketWatchInterval) clearInterval(marketWatchInterval);
    });

    return {
      sessionLoading,
    };
  },
});
</script>
<style scoped>
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
